// client/src/components/analytics/PostAnalytics.jsx
// Per-post analytics with full-size media display, platform breakdown, and interaction details

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  fetchPostAnalytics,
  formatMetricNumber,
  PLATFORM_META,
} from '../../services/analyticsApi';
import MediaTile from '../media/MediaTile';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import SearchFilterBar from '../shared/SearchFilterBar';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
];

function PostAnalytics() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [days, setDays] = useState(30);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadPosts = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);

    try {
      const data = await fetchPostAnalytics(tenantId, { days, limit: 50 });
      setPosts(data.posts || []);
      setPagination(data.pagination || { total: 0, hasMore: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, days]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const filteredPosts = useMemo(() => {
    let result = posts;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((p) => {
        const content = (p.content || "").toLowerCase();
        const platforms = (p.platformDetails || []).map(pd => pd.platform).join(" ").toLowerCase();
        return content.includes(q) || platforms.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((p) => p.postedOn && new Date(p.postedOn) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((p) => p.postedOn && new Date(p.postedOn) <= to);
    }
    return result;
  }, [posts, searchTerm, dateFrom, dateTo]);

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#fff' }}>Post Analytics</h2>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          style={{
            background: '#253866', color: '#fff', border: '1px solid #ffa500',
            borderRadius: 6, padding: '4px 8px', fontSize: '0.85rem', cursor: 'pointer',
          }}
        >
          {PERIOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value} style={{ background: '#1a1a2e', color: '#fff' }}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,50,50,0.15)', border: '1px solid #ff4444', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#ff8888', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); }}
        searchPlaceholder="Search posts\u2026"
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div className="spinner-border text-cobalt" role="status"></div>
          <p style={{ marginTop: 12, color: '#a8a8a8' }}>Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#a8a8a8' }}>
          <h5 style={{ color: '#e6e6e6' }}>No Posts Found</h5>
          <p style={{ fontSize: '0.85rem' }}>No published posts found for this period.</p>
        </div>
      ) : (
        <>
          <p style={{ color: '#a8a8a8', fontSize: '0.8rem', marginBottom: 12 }}>
            Showing {filteredPosts.length} of {pagination.total} posts
          </p>

          {filteredPosts.map((post) => (
            <PostCard
              key={post.postId}
              post={post}
              expanded={expandedPost === post.postId}
              onToggle={() => setExpandedPost(expandedPost === post.postId ? null : post.postId)}
            />
          ))}
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(`/${tenant}/analytics`) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ============================================================================
// Post Card with full-size media, content, and expandable interactions
// ============================================================================
function PostCard({ post, expanded, onToggle }) {
  const hasMedia = post.mediaUrls?.length > 0;
  const postedDate = post.postedOn
    ? new Date(post.postedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <div style={{ background: '#2a2a2a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      {/* Full-size media display */}
      {hasMedia && (
        <div style={{ display: 'grid', gridTemplateColumns: post.mediaUrls.length === 1 ? '1fr' : 'repeat(2, 1fr)', gap: 2 }}>
          {post.mediaUrls.map((url, i) => {
            const isVideo = /\.(mp4|mov|webm|m4v)/i.test(url);
            return (
              <div key={i} style={{ width: '100%' }}>
                <MediaTile src={url} isVideo={isVideo} />
              </div>
            );
          })}
        </div>
      )}

      {/* Content section */}
      <div style={{ padding: '12px 14px' }}>
        {/* Date */}
        <div style={{ fontSize: '0.7rem', color: '#ffa500', marginBottom: 4 }}>{postedDate}</div>

        {/* Post text */}
        <p style={{ fontSize: '0.85rem', lineHeight: 1.4, color: '#e6e6e6', margin: '0 0 10px 0' }}>
          {(post.content || '').substring(0, 300) || '(No text)'}
          {post.content?.length > 300 ? '...' : ''}
        </p>

        {/* Platform badges */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {post.platformDetails?.map((pd, i) => {
            const meta = PLATFORM_META[pd.platform] || { name: pd.platform, color: '#666', icon: 'bi-globe' };
            return (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: meta.color, color: '#fff', borderRadius: 4,
                padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600,
              }}>
                <i className={`bi ${meta.icon}`}></i>
                {meta.name}
                {pd.status === 'failed' && <i className="bi bi-x-circle" style={{ marginLeft: 2 }}></i>}
              </span>
            );
          })}
        </div>

        {/* Metrics row */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <MetricPill color="#E91E63" value={post.totalLikes} label="Likes" />
          <MetricPill color="#2196F3" value={post.totalComments} label="Comments" />
          <MetricPill color="#FF9800" value={post.totalShares} label="Shares" />
          {post.totalReach > 0 && <MetricPill color="#4CAF50" value={post.totalReach} label="Reach" />}
          {post.totalVideoViews > 0 && <MetricPill color="#f44336" value={post.totalVideoViews} label="Views" />}
        </div>

        {/* Expand toggle */}
        <button
          onClick={onToggle}
          style={{
            background: 'none', border: '1px solid #ffa500', borderRadius: 6,
            color: '#ffa500', padding: '4px 12px', fontSize: '0.75rem',
            cursor: 'pointer', width: '100%',
          }}
        >
          {expanded ? 'Hide Details' : 'Show Platform Details'}
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} ml-1`}></i>
        </button>

        {/* Expanded: per-platform breakdown */}
        {expanded && post.platformDetails?.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #444' }}>
            <PlatformInteractionChart platformDetails={post.platformDetails} />
            <div style={{ marginTop: 8 }}>
              {post.platformDetails.map((pd, i) => {
                const meta = PLATFORM_META[pd.platform] || { name: pd.platform, color: '#666', icon: 'bi-globe' };
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                    borderBottom: '1px solid #333', fontSize: '0.8rem',
                  }}>
                    <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '0.9rem' }}></i>
                    <span style={{ fontWeight: 600, minWidth: 70, color: '#e6e6e6' }}>{meta.name}</span>
                    <span style={{ flex: 1, color: '#a8a8a8' }}>
                      {pd.status === 'posted' ? (
                        pd.hasMetrics ? (
                          <>
                            {formatMetricNumber(pd.likes)} likes, {formatMetricNumber(pd.comments)} comments, {formatMetricNumber(pd.shares)} shares
                            {pd.reach > 0 && <>, {formatMetricNumber(pd.reach)} reach</>}
                          </>
                        ) : (
                          <span style={{ fontStyle: 'italic', color: '#777' }}>Metrics pending...</span>
                        )
                      ) : (
                        <span style={{ color: '#ff6b6b' }}>Failed</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Per-post platform interaction chart
// ============================================================================
function PlatformInteractionChart({ platformDetails }) {
  const data = platformDetails
    .filter(pd => pd.status === 'posted' && pd.hasMetrics)
    .map(pd => {
      const meta = PLATFORM_META[pd.platform] || { name: pd.platform, color: '#666' };
      return {
        name: meta.name,
        likes: pd.likes,
        comments: pd.comments,
        shares: pd.shares,
      };
    });

  if (!data.length) return null;

  return (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#a8a8a8' }} />
        <YAxis tick={{ fontSize: 10, fill: '#a8a8a8' }} />
        <Tooltip
          contentStyle={{ background: '#1a1a1a', border: '1px solid #444', borderRadius: 6, fontSize: 12, color: '#fff' }}
        />
        <Bar dataKey="likes" name="Likes" fill="#E91E63" radius={[2, 2, 0, 0]} stackId="a" />
        <Bar dataKey="comments" name="Comments" fill="#2196F3" radius={[0, 0, 0, 0]} stackId="a" />
        <Bar dataKey="shares" name="Shares" fill="#FF9800" radius={[2, 2, 0, 0]} stackId="a" />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ============================================================================
// Metric Pill
// ============================================================================
function MetricPill({ color, value, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }}></div>
      <span style={{ fontSize: '0.8rem', color: '#fff', fontWeight: 600 }}>
        {formatMetricNumber(value)}
      </span>
      <span style={{ fontSize: '0.7rem', color: '#a8a8a8' }}>{label}</span>
    </div>
  );
}

export default PostAnalytics;
